import {useSearchParams} from "react-router-dom";
import {DataRequestParams, DataRequestState, DataTable, useDataRequest, useDebouncedFilter} from "../../data";
import FilterTableKontakteView, {KontakteViewFilter} from "./FilterTableKontakteView";
import {useEffect, useMemo, useRef, useState, useCallback} from "react";
import {Box, capitalize, Paper, Typography, IconButton, Tooltip, Collapse, Stack} from "@mui/material";
import {useSnackbar} from "notistack";
import {ViewKontakt} from "../../../api";
import {GridColDef, GridRenderCellParams, GridRowSelectionModel} from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import {useKontakteHandling} from "../../../hooks/kontakte/useKontakteHandling";
import {kontakteStyles} from "../../../styles/kontakte/kontakte.styles";
import {useParams} from "react-router";
import KontaktDetailsModal from "./KontaktDetailsModal";
import {useDeleteKontaktaufnahme, useGetKontaktaufnahmenByKontaktaufgabeId, useSearchKontakteView} from "../../../client/hooks";
import {useRescheduleKontaktaufnahme} from "../../../hooks/kontakte/useRescheduleKontaktaufnahme";
import {useQueryClient} from "react-query";
import {useAudioDownload} from "../../../hooks/kontakte/useAudioDownload";
import {usePdfDownload} from "../../../hooks/kontakte/usePdfDownload";
import {ActionButtonsBar, getKontaktActionItems, getKontaktBulkActionDefinitions} from "../KontaktActionButtons";
import {useLoadingWithMinDuration} from "../../../hooks/useLoadingWithMinDuration";
import ButtonExportAuswertungAufgabe from "../../aufgaben/ButtonExportAuswertungAufgabe";
import ButtonShowTestCallDialog from "../../aufgaben/ButtonShowTestCallDialog";
import ImportContactsButton from "../ImportContactsButton";
import RescheduleDialog from "../RescheduleDialog";
import DeleteConfirmDialog from "../DeleteConfirmDialog";
import {useUserMandant} from "../../../hooks/useUserMandant";
import {useAufgabe} from "../../../client/queries";

interface KontaktTarget {
    ids: string[];
    name?: string;
}

export default function TableKontakteView() {
    const [searchParams] = useSearchParams();
    const {translation, loadKontakteView} = useKontakteHandling();
    const [selectedKontakt, setSelectedKontakt] = useState<ViewKontakt | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [initialKontaktaufnahmeId, setInitialKontaktaufnahmeId] = useState<string | undefined>(undefined);
    const [deleteTarget, setDeleteTarget] = useState<KontaktTarget | null>(null);
    const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>([]);
    const [rescheduleTarget, setRescheduleTarget] = useState<KontaktTarget | null>(null);
    const [loadingActions, setLoadingActions] = useState<Set<string>>(new Set());
    const [isBulkLoading, setIsBulkLoading] = useState(false);
    const deleteKontaktaufnahme = useDeleteKontaktaufnahme();
    const rescheduleKontaktaufnahme = useRescheduleKontaktaufnahme();
    const queryClient = useQueryClient();
    const { downloadAudio, downloadMultipleAudio } = useAudioDownload();
    const downloadPdf = usePdfDownload();
    const { execute: executeWithMinDuration } = useLoadingWithMinDuration(500);
    const { isMandantAnrufer } = useUserMandant();
    const { enqueueSnackbar } = useSnackbar();

    const {aufgabeId = ""} = useParams<{
        aufgabeId: string;
    }>();
    const aufgabe = useAufgabe(aufgabeId);
    const request: DataRequestState<KontakteViewFilter> = useDataRequest<KontakteViewFilter>({
        filter: {
            filtered: searchParams.get("filter") ?? "",
            search: searchParams.get("search") ?? "",
        },
        sort: [{ field: "kontaktaufnahmeEnde", direction: "desc" }],
    });

    const [{search}, setField] = useDebouncedFilter(request);

    const filter = useMemo(() => {
        return searchParams.get("filter");
    }, [searchParams]);


    const searchKontakteView = useSearchKontakteView();
    const getKontaktaufnahmenByKontaktaufgabeId = useGetKontaktaufnahmenByKontaktaufgabeId();
    const deepLinkHandled = useRef(false);
    useEffect(() => {
        if (deepLinkHandled.current || !aufgabeId) return;
        const kontaktaufnahmeId = searchParams.get("kontaktaufnahmeId");
        if (!kontaktaufnahmeId) return;
        deepLinkHandled.current = true;

        (async () => {
            const kontaktaufnahmenResult = await getKontaktaufnahmenByKontaktaufgabeId(kontaktaufnahmeId);
            const kontakteAufgabenId = kontaktaufnahmenResult.kontaktaufnahmen?.[0]?.kontakteAufgabenId;
            if (!kontakteAufgabenId) throw new Error("kontakteAufgabenId not found");

            const viewResult = await searchKontakteView(aufgabeId, {search: kontakteAufgabenId, limit: 1});
            const match = viewResult.items.find((k) => k.id === kontakteAufgabenId);
            if (!match) throw new Error("view_kontakte row not found");

            setInitialKontaktaufnahmeId(kontaktaufnahmeId);
            setSelectedKontakt(match);
            setIsModalOpen(true);
        })().catch(() => {
            enqueueSnackbar(translation('share-link-not-found'), {variant: "error"});
        });
    }, [aufgabeId, searchParams, searchKontakteView, getKontaktaufnahmenByKontaktaufgabeId, enqueueSnackbar, translation]);

    const handleRowClick = (kontakt: ViewKontakt) => {
        setInitialKontaktaufnahmeId(undefined);
        setSelectedKontakt(kontakt);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedKontakt(null);
        setInitialKontaktaufnahmeId(undefined);
    };

    const onAudioDownload = useCallback(async (kontakt: ViewKontakt) => {
        if (!kontakt.kontaktaufnahmeId) return;

        const actionKey = `${kontakt.kontaktaufnahmeId}-audio`;
        setLoadingActions(prev => new Set(prev).add(actionKey));

        try {
            await executeWithMinDuration(async () => {
                await downloadAudio(kontakt.kontaktaufnahmeId, kontakt.kontaktVorname, kontakt.kontaktNachname);
            });
        } finally {
            setLoadingActions(prev => {
                const newSet = new Set(prev);
                newSet.delete(actionKey);
                return newSet;
            });
        }
    }, [downloadAudio, executeWithMinDuration]);

    const handleBulkAudioDownload = useCallback(async () => {
        if (selectedRowIds.length === 0) return;
        setIsBulkLoading(true);
        try {
            await downloadMultipleAudio(selectedRowIds as string[]);
        } finally {
            setIsBulkLoading(false);
        }
    }, [downloadMultipleAudio, selectedRowIds]);

    const onPdfDownload = useCallback(async (kontakt: ViewKontakt) => {
        if (!kontakt.kontaktaufnahmeId) return;

        const actionKey = `${kontakt.kontaktaufnahmeId}-pdf`;
        setLoadingActions(prev => new Set(prev).add(actionKey));

        try {
            await executeWithMinDuration(async () => {
                await downloadPdf(kontakt.kontaktaufnahmeId);
            });
        } finally {
            setLoadingActions(prev => {
                const newSet = new Set(prev);
                newSet.delete(actionKey);
                return newSet;
            });
        }
    }, [downloadPdf, executeWithMinDuration]);

    const handleBulkPdfDownload = useCallback(async () => {
        if (selectedRowIds.length === 0) return;
        setIsBulkLoading(true);
        try {
            // Ensure all IDs are strings and not null/undefined
            const ids = (selectedRowIds as (string | number)[])
                .filter(id => id != null)
                .map(id => String(id));
            await downloadPdf(ids);
        } catch (error) {
            console.error('Error in bulk PDF download:', error);
        } finally {
            setIsBulkLoading(false);
        }
    }, [downloadPdf, selectedRowIds]);

    const handleDeleteClick = (kontakt: ViewKontakt) => {
        if (!kontakt.kontaktaufnahmeId) return;
        setDeleteTarget({
            ids: [kontakt.kontaktaufnahmeId],
            name: `${kontakt.kontaktVorname} ${kontakt.kontaktNachname}`
        });
    };

    const handleBulkDeleteClick = () => {
        setDeleteTarget({ ids: selectedRowIds as string[] });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        const isBulk = deleteTarget.ids.length > 1;
        if (isBulk) setIsBulkLoading(true);

        const failedIds: string[] = [];
        for (const id of deleteTarget.ids) {
            try {
                await deleteKontaktaufnahme(id);
            } catch (error) {
                console.error(`Error deleting contact ${id}:`, error);
                failedIds.push(id);
            }
        }

        await queryClient.invalidateQueries(["kontakte-view"]);

        if (isBulk) {
            setSelectedRowIds(failedIds);
            if (failedIds.length > 0) {
                enqueueSnackbar(translation('bulk-delete-error', { count: failedIds.length }), { variant: 'error' });
            }
            setIsBulkLoading(false);
        } else if (failedIds.length > 0) {
            enqueueSnackbar(translation('delete-error'), { variant: 'error' });
        }

        setDeleteTarget(null);
    };

    const handleDeleteCancel = () => {
        setDeleteTarget(null);
    };

    const handleRescheduleClick = (kontakt: ViewKontakt) => {
        if (!kontakt.kontaktaufnahmeId) return;
        setRescheduleTarget({
            ids: [kontakt.kontaktaufnahmeId],
            name: `${kontakt.kontaktVorname} ${kontakt.kontaktNachname}`
        });
    };

    const handleBulkRescheduleClick = () => {
        setRescheduleTarget({ ids: selectedRowIds as string[] });
    };

    const handleReschedule = async (instant: boolean) => {
        if (!rescheduleTarget) return;
        const isBulk = rescheduleTarget.ids.length > 1;
        if (isBulk) setIsBulkLoading(true);

        const failedIds: string[] = [];
        for (const id of rescheduleTarget.ids) {
            try {
                await rescheduleKontaktaufnahme(
                    id,
                    instant ? { immediate: true } : undefined
                );
            } catch (error) {
                console.error(`Error rescheduling contact ${id}:`, error);
                failedIds.push(id);
            }
        }

        await queryClient.invalidateQueries(["kontakte-view"]);

        if (isBulk) {
            setSelectedRowIds(failedIds);
            if (failedIds.length > 0) {
                enqueueSnackbar(translation('bulk-reschedule-error', { count: failedIds.length }), { variant: 'error' });
            }
            setIsBulkLoading(false);
        }

        setRescheduleTarget(null);
    };

    const handleRescheduleCancel = () => {
        setRescheduleTarget(null);
    };

    return (
        <Paper sx={kontakteStyles.tableKontakteView.paper}>
            <Box sx={kontakteStyles.tableKontakteView.headerBoxWrapper}>
                <Typography variant="h5">
                    {capitalize(translation('contacts'))}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {aufgabeId && isMandantAnrufer && <ButtonShowTestCallDialog aufgabeId={aufgabeId} language={aufgabe?.sprache} />}
                    {aufgabeId && <ButtonExportAuswertungAufgabe aufgabeId={aufgabeId} />}
                    {aufgabeId && <ImportContactsButton aufgabeId={aufgabeId} />}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <FilterTableKontakteView setField={setField} search={search}/>
            </Box>

            <Collapse in={selectedRowIds.length > 0}>
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        mb: 2,
                        backgroundColor: (theme) => theme.palette.action.hover,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mr: 2 }}>
                            {selectedRowIds.length} {translation(selectedRowIds.length === 1 ? 'contact' : 'contacts')} ausgewählt
                        </Typography>
                        <ActionButtonsBar
                            actions={getKontaktBulkActionDefinitions(
                                handleBulkDeleteClick,
                                handleBulkRescheduleClick,
                                handleBulkAudioDownload,
                                handleBulkPdfDownload,
                                {
                                    delete: translation('delete'),
                                    reschedule: translation('reschedule'),
                                    downloadAudio: translation('download-audio'),
                                    downloadPdf: translation('download-pdf'),
                                },
                                isBulkLoading
                            )}
                        />
                    </Stack>
                    <Tooltip title={translation('cancel')}>
                        <IconButton onClick={() => setSelectedRowIds([])} disabled={isBulkLoading}>
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Paper>
            </Collapse>

            <Box sx={{
                ...kontakteStyles.tableKontakteView.box,
                '& .MuiDataGrid-actionsCell': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 0.5,
                },
            }}>

                <DataTable<ViewKontakt, KontakteViewFilter>
                    name="table-kontakte-view"
                    queryKey={["kontakte-view", aufgabeId]}
                    loadData={(params: DataRequestParams<KontakteViewFilter>) => loadKontakteView(aufgabeId, params, request.filter)}
                    request={request}
                    getRowId={(row) => row.kontaktaufnahmeId}
                    columns={useColumns(handleDeleteClick, handleRescheduleClick, onAudioDownload, onPdfDownload, loadingActions, aufgabeId)}
                    size="large"
                    loading={isBulkLoading}
                    initialFilters={filter ? JSON.parse(filter) : undefined}
                    disableRowSelectionOnClick={true}
                    checkboxSelection
                    rowSelectionModel={selectedRowIds}
                    onRowSelectionModelChange={(newSelection) => setSelectedRowIds(newSelection)}
                    onRowClick={(params) => handleRowClick(params.row)}
                    setField={setField}
                />
            </Box>
            <KontaktDetailsModal
                key={selectedKontakt?.kontaktaufnahmeId ?? "none"}
                open={isModalOpen}
                kontakt={selectedKontakt}
                aufgabeId={aufgabeId}
                onClose={handleCloseModal}
                initialKontaktaufnahmeId={initialKontaktaufnahmeId}
            />
            <DeleteConfirmDialog
                open={!!deleteTarget}
                onCancel={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                translations={{
                    title: translation('delete-confirmation'),
                    question: deleteTarget && deleteTarget.ids.length > 1
                        ? translation('delete-multiple-kontaktaufnahme-confirmation', { count: deleteTarget.ids.length })
                        : translation('delete-kontaktaufnahme-confirmation', { name: deleteTarget?.name ?? '' }),
                    cancel: translation('cancel'),
                    delete: translation('delete'),
                }}
            />
            <RescheduleDialog
                open={!!rescheduleTarget}
                onCancel={handleRescheduleCancel}
                onReschedule={handleReschedule}
                translations={{
                    title: translation('reschedule-kontaktaufnahme'),
                    question: rescheduleTarget && rescheduleTarget.ids.length > 1
                        ? translation('reschedule-multiple-kontaktaufnahme-confirmation', { count: rescheduleTarget.ids.length })
                        : translation('reschedule-kontaktaufnahme-question', { name: rescheduleTarget?.name ?? '' }),
                    cancel: translation('cancel'),
                    asPlanned: translation('as-planned'),
                    instant: translation('instant')
                }}
            />
        </Paper>
    );
}

function useColumns(
    onDeleteClick: (kontakt: ViewKontakt) => void,
    onRescheduleClick: (kontakt: ViewKontakt) => void,
    onAudioDownload: (kontakt: ViewKontakt) => void,
    onPdfDownload: (kontakt: ViewKontakt) => void,
    loadingActions: Set<string>,
    aufgabeId: string,
): Array<GridColDef<ViewKontakt>> {
    const {translation} = useKontakteHandling();
    const {enqueueSnackbar} = useSnackbar();

    const onShare = useCallback((kontakt: ViewKontakt) => {
        if (!aufgabeId || !kontakt.kontaktaufnahmeId) return;
        const url = `${window.location.origin}/aufgaben/${aufgabeId}/kontakte?kontaktaufnahmeId=${kontakt.kontaktaufnahmeId}`;
        navigator.clipboard.writeText(url).then(() => {
            enqueueSnackbar(translation('share-link-copied'), {variant: "success"});
        });
    }, [aufgabeId, enqueueSnackbar, translation]);

    const dateValueGetter = (value: string | number | Date | null | undefined) => {
        if (value == null || value === "") return null;
        const date = new Date(value as string | number | Date);
        return isNaN(date.getTime()) ? null : date;
    };

    const renderDateTimeCell = (params: GridRenderCellParams<ViewKontakt, Date | null>) => {
        const date = params.value as Date | null;
        if (date && !isNaN(date.getTime())) {
            const formattedDate = date.toLocaleDateString(undefined, {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
            });
            const formattedTime = date.toLocaleTimeString(undefined, {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            });
            return `${formattedDate} ${formattedTime}`;
        }
        return "";
    };

    return useMemo(
        () => [
            {
                field: "kontaktNachname",
                headerName: capitalize(translation("contact-attributes-name")),
                flex: 1,
            },
            {
                field: "kontaktVorname",
                headerName: capitalize(translation("contact-attributes-first-name")),
                flex: 1,
            },
            {
                field: "kontaktTelefon",
                headerName: capitalize(translation("contact-attributes-phone")),
                flex: 1,
            },
            {
                field: "kontaktExternalId",
                headerName: capitalize(translation("contact-attributes-external-id")),
                flex: 1,
            },
            {
                field: "kontaktaufnahmeEnde",
                headerName: capitalize(translation("contact-attributes-end-time")),
                flex: 1,
                type: "dateTime",
                valueGetter: dateValueGetter,
                renderCell: renderDateTimeCell,
            },
            {
                field: "kontaktaufnahmeStartPlanned",
                headerName: capitalize(translation("contact-attributes-planned-contact-time")),
                flex: 1,
                type: "dateTime",
                valueGetter: dateValueGetter,
                renderCell: renderDateTimeCell,
            },
            {
                field: "kontaktaufnahmeStart",
                headerName: capitalize(translation("contact-attributes-actual-contact-time")),
                flex: 1,
                type: "dateTime",
                valueGetter: dateValueGetter,
                renderCell: renderDateTimeCell,
            },
            {
                field: "kontaktaufnahmeStatus",
                headerName: capitalize(translation("contact-attributes-last-call-status")),
                flex: 1,
            },
            {
                field: "auswertungRanking",
                headerName: capitalize(translation("contact-attributes-ranking")),
                flex: 1,
                valueFormatter: (value) => {
                    if (value == null) return '-';
                    return Number(value).toFixed(2);
                },
            },
            {
                field: "kontaktaufnahmeVersuchNummer",
                headerName: capitalize(translation("contact-attributes-attempt-number")),
                flex: 1,
                valueFormatter: (value) => {
                    if (value == null) return '-';
                    return value;
                },
            },
            {
                field: "kontaktAufgabeStatus",
                headerName: capitalize(translation("contact-attributes-status")),
                flex: 1,
            },
            {
                field: 'actions',
                type: 'actions',
                headerName: capitalize(translation('actions')),
                minWidth: 200,
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                cellClassName: 'actions',
                getActions: (params) => {
                    // Only show buttons if kontaktaufnahmeId exists
                    if (!params.row.kontaktaufnahmeId) {
                        return [];
                    }

                    const audioKey = `${params.row.kontaktaufnahmeId}-audio`;
                    const pdfKey = `${params.row.kontaktaufnahmeId}-pdf`;

                    return getKontaktActionItems(
                        params.row,
                        onAudioDownload,
                        onPdfDownload,
                        onDeleteClick,
                        onRescheduleClick,
                        {
                            downloadAudio: translation('download-audio'),
                            downloadPdf: translation('download-pdf'),
                            delete: translation('delete'),
                            reschedule: translation('reschedule'),
                            share: translation('share-link'),
                        },
                        {
                            isAudioLoading: loadingActions.has(audioKey),
                            isPdfLoading: loadingActions.has(pdfKey)
                        },
                        onShare,
                    );
                },
            },
        ],
        [translation, onDeleteClick, onRescheduleClick, onAudioDownload, onPdfDownload, loadingActions, onShare]
    );
}
